import type { UserMenu } from "@/modules/auth";

/**
 * Check if a user has permission to access a specific route
 * @param userMenus - Array of user menu items containing accessible paths
 * @param routePath - The route path to check permission for
 * @returns boolean indicating if user has access
 */
export const hasRoutePermission = (
  userMenus: UserMenu[],
  routePath: string
): boolean => {
  if (!userMenus || userMenus.length === 0) {
    return false;
  }

  // Check if the route path matches any menu path
  return userMenus.some((menu) => {
    // Exact match
    if (menu.path === routePath) {
      return true;
    }

    // Check if the route is a child of a menu path
    // e.g., /products/create is a child of /products
    if (routePath.startsWith(menu.path) && menu.path !== "/") {
      return true;
    }

    // Special handling for root path
    if (menu.path === "/" && routePath === "/") {
      return true;
    }

    return false;
  });
};

/**
 * Get the parent route path for nested routes
 * e.g., /users/buyers -> /users
 *       /products/create -> /products
 * @param path - The route path
 * @returns parent path or null if at root
 */
export const getParentRoutePath = (path: string): string | null => {
  const segments = path.split("/").filter(Boolean);

  if (segments.length <= 1) {
    return null;
  }

  return "/" + segments.slice(0, -1).join("/");
};

/**
 * Check if user has permission for route or its parent
 * Useful for nested routes like /users/buyers where permission might be on /users
 */
export const hasRouteOrParentPermission = (
  userMenus: UserMenu[],
  routePath: string
): boolean => {
  // Check current path
  if (hasRoutePermission(userMenus, routePath)) {
    return true;
  }

  // Check parent path
  const parentPath = getParentRoutePath(routePath);

  if (parentPath && hasRoutePermission(userMenus, parentPath)) {
    return true;
  }

  return false;
};
